import { createGetQualificationsQueryOptions } from "@src/appV2/Admin/Qualifications/api/useGetQualifications";
import { createFetchWorkplaceWorkerTypesQueryOptions } from "@src/appV2/FacilityDrawer/api/useFetchWorkplaceWorkerTypes";
import { type QueryClient } from "@tanstack/react-query";

import { getMonthlyCalendarDateRange } from "../MonthlyView/utils/monthlyCalendarHelpers";
import { createDayShiftsQueryOptions } from "./api/useGetDayShifts";
import { createFacilityShiftCountsQueryOptions } from "./api/useGetFacilityShiftCounts";
import { createInitialDateRange } from "./hooks/useDailyViewDataHelpers";

interface PrefetchScheduleDataParams {
  queryClient: QueryClient;
  facilityId: string;
  timezone?: string;
  selectedDate?: Date;
}

function getVisibleWorkerTypeNames(
  workerTypes: Array<{ contracted?: boolean; name: string; visible: boolean }>
): string[] {
  return workerTypes
    .filter((workerType) => workerType.visible && workerType.contracted !== false)
    .map((workerType) => workerType.name);
}

async function prefetchShiftCounts(params: {
  agentRequests: string[];
  dateFilter: { start: string; end: string };
  facilityId: string;
  queryClient: QueryClient;
  timezone: string;
}) {
  const { agentRequests, dateFilter, facilityId, queryClient, timezone } = params;

  await Promise.allSettled(
    agentRequests.map((agentRequest) =>
      queryClient.prefetchQuery(
        createFacilityShiftCountsQueryOptions({
          facilityId,
          dateFilter,
          timezone,
          agentReq: agentRequest,
          enabled: true,
        })
      )
    )
  );
}

export async function prefetchScheduleData(params: PrefetchScheduleDataParams): Promise<void> {
  const { queryClient, facilityId, selectedDate = new Date() } = params;

  if (!facilityId) {
    return;
  }

  const timezone = params.timezone ?? "UTC";
  const dailyDateRange = createInitialDateRange(selectedDate);
  const monthlyDateRange = getMonthlyCalendarDateRange(selectedDate);

  const workerTypesPromise = queryClient.fetchQuery(
    createFetchWorkplaceWorkerTypesQueryOptions({
      workplaceId: facilityId,
      showHidden: true,
    })
  );
  const qualificationsPromise = queryClient.fetchQuery(
    createGetQualificationsQueryOptions({
      filter: {
        workplaceId: facilityId,
        enabled: true,
      },
    })
  );

  await Promise.allSettled([
    queryClient.prefetchQuery(
      createDayShiftsQueryOptions({
        date: selectedDate,
        facilityId,
        timezone,
      })
    ),
    queryClient.prefetchQuery(
      createFacilityShiftCountsQueryOptions({
        facilityId,
        dateFilter: dailyDateRange,
        timezone,
        enabled: true,
      })
    ),
    queryClient.prefetchQuery(
      createFacilityShiftCountsQueryOptions({
        facilityId,
        dateFilter: monthlyDateRange,
        timezone,
        enabled: true,
      })
    ),
    workerTypesPromise,
    qualificationsPromise,
  ]);

  const [workerTypesResult, qualificationsResult] = await Promise.allSettled([
    workerTypesPromise,
    qualificationsPromise,
  ]);

  if (workerTypesResult.status !== "fulfilled") {
    return;
  }

  const visibleDailyWorkerTypeNames = getVisibleWorkerTypeNames(
    workerTypesResult.value.data.workerTypes
  );

  await prefetchShiftCounts({
    agentRequests: visibleDailyWorkerTypeNames,
    dateFilter: dailyDateRange,
    facilityId,
    queryClient,
    timezone,
  });

  if (qualificationsResult.status !== "fulfilled") {
    return;
  }

  const monthlyQualificationNames = new Set(
    qualificationsResult.value.data.map((qualification) => qualification.attributes.name)
  );
  const visibleMonthlyWorkerTypeNames = visibleDailyWorkerTypeNames.filter((workerTypeName) =>
    monthlyQualificationNames.has(workerTypeName)
  );

  await prefetchShiftCounts({
    agentRequests: visibleMonthlyWorkerTypeNames,
    dateFilter: monthlyDateRange,
    facilityId,
    queryClient,
    timezone,
  });
}
